import { Box, Link, Stack, Table, TableBody, TableRow } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from 'src/components/emptyState';
import { FilterList } from 'src/components/filterList';
import { ActionBar, Header } from 'src/components/header';
import { Pagination } from 'src/components/pagination/Pagination';
import { Progress } from 'src/components/progress/Progress';
import { SubHeaderWithSorting } from 'src/components/subHeader';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useParamQuery } from 'src/hooks';
import {
  IComponent,
  IComponentType,
  IExpiredStatus,
  IFilter,
  IStatus,
  IStatusLabel,
} from 'src/interfaces';
import {
  escapeSearchTerms,
  getExpiryStatusMessage,
  getReplacementComponentFilter,
  getSelectableComponentTypes,
  isEligibleForSelection,
  lang,
} from 'src/utils';

import { useListComponentsPage } from './hooks/ListComponentsPage.hook';
import {
  CustomTableCell,
  CustomTableHead,
  CustomTableHeadCell,
  TableBodyContainer,
  TableWrapper,
} from './ListComponentsPage.styled';
import { ComponentPreviewSidebar } from './modules/componentPreviewSidebar/ComponentPreviewSidebar';
import { ComponentTableRow } from './modules/componentTableRow/ComponentTableRow';
import { EmptyComponents } from './modules/EmptyComponents/EmptyComponents';

export interface CommonComponentsListProps {
  componentDetails?: IComponent;
  componentTypes?: IComponentType[];
}

export const ListComponentsPage = (props: CommonComponentsListProps) => {
  const navigate = useNavigate();
  const isReplacementMode = props.componentDetails !== undefined;
  const { getParamsValue } = useParamQuery();

  const {
    isLoadingComponents,
    selectedSortOption,
    onSortChange,
    hasActiveFilters,
    componentTypesPage,
    isLoadingComponentTypes,
    errorLoadingComponentTypes,
    updateComponentTypesPage,
    components,
    totalCount,
    totalPages,
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    selectedCard,
    handleCardClick,
    hasSidebarPreviewLoaded,
    isLoadingSidebarPreview,
    onHandleComponentPreviewSidebarClose,
    activeFilters,
  } = useListComponentsPage(
    isReplacementMode && props.componentDetails
      ? getReplacementComponentFilter(
          props.componentDetails,
          props?.componentTypes,
          getParamsValue,
        )
      : undefined,
  );

  const columns = useMemo(
    () => [
      { key: 'name', label: 'Component name', width: '30%' },
      { key: 'status', label: 'Status', width: '20%' },
      { key: 'type', label: 'Type', width: '20%' },
      { key: 'size', label: 'Size', width: '20%' },
      { key: 'menu', label: '', width: '10%' },
    ],
    [],
  );

  const selectableComponentTypes = useMemo(
    () => getSelectableComponentTypes(componentTypesPage, isReplacementMode),
    [componentTypesPage, isReplacementMode],
  );

  const filters: IFilter[] = [
    {
      label: 'Component type',
      options: selectableComponentTypes,
      totalPages: componentTypesPage?.totalPages,
      currPage: componentTypesPage.page,
      isLoading: isLoadingComponentTypes,
      isError: errorLoadingComponentTypes ? true : false,
    },

    {
      label: lang.componentTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
      customElement: (
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate(PATHS.COMPONENT_TAGS_MANAGE)}>
          Manage tags
        </Link>
      ),
    },
  ];

  if (!isReplacementMode) {
    filters.splice(1, 0, {
      label: 'Status',
      options: Object.values(IStatus).map((value) => ({
        value: value,
        label: IStatusLabel[`${value}`],
      })),
    });
    filters.push({
      label: lang.ownerOperator.ownerOperatorHeader,
      options: [{ value: 'true', label: lang.allowed }],
    });
  }

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );
  const {
    onChangeKeyword,
    onChangeParams,
    onRemoveKeyword,
    onChangeFilter,
    onReset,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
  } = useFilterAndSorting(filters, [], isFuzzySearch, setIsFuzzySearch);

  const onHandleChangeKeyword = useCallback(
    (value: string | string[]) => {
      if (isFuzzySearch) {
        const escaped = escapeSearchTerms(value, getParamsValue('name.regexp'));
        // we should avoid the terms re-escaping
        onChangeParams('name.regexp', [...new Set(escaped)] as string[]);
      } else {
        onChangeKeyword(value);
      }
      onHandleComponentPreviewSidebarClose();
    },
    [
      onChangeKeyword,
      onChangeParams,
      onHandleComponentPreviewSidebarClose,
      isFuzzySearch,
      getParamsValue,
    ],
  );

  const onHandleRemoveKeyword = useCallback(
    (value: string) => {
      onRemoveKeyword(value);
      onHandleComponentPreviewSidebarClose();
    },
    [onRemoveKeyword, onHandleComponentPreviewSidebarClose],
  );

  const onHandleChangeFilter = useCallback(
    (key: string, value: string | string[]) => {
      onChangeFilter(key, value);
      onHandleComponentPreviewSidebarClose();
    },
    [onChangeFilter, onHandleComponentPreviewSidebarClose],
  );

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  const onHandleReset = useCallback(() => {
    onReset();
    onHandleComponentPreviewSidebarClose();
  }, [onHandleComponentPreviewSidebarClose, onReset]);

  const showEmptyState =
    !components.length && !isLoadingComponents && !hasActiveFilters;

  const isNotGlobalComponent = isEligibleForSelection(selectedCard);

  const previewSidebar = selectedCard && (
    <Box flex={0.4} width="max(300px, 10vw)">
      <ComponentPreviewSidebar
        selectedCard={selectedCard}
        isLoadingSidebarPreview={isLoadingSidebarPreview}
        hasSidebarPreviewLoaded={hasSidebarPreviewLoaded}
        onClose={onHandleComponentPreviewSidebarClose}
        isReplacementComponent={isReplacementMode}
        componentId={
          isReplacementMode ? props.componentDetails?.id.toString() : undefined
        }
        replacementId={isReplacementMode ? selectedCard?.id : undefined}
      />
    </Box>
  );

  return showEmptyState ? (
    <EmptyComponents message={lang.noComponents} showButton={true} />
  ) : (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={4}
      style={{ padding: 'unset', height: '100%' }}>
      <Box flex={1} sx={{ padding: '20px' }}>
        {isReplacementMode ? (
          <Header title={lang.selectReplacementComponent} />
        ) : (
          <Header
            actionBar={
              <ActionBar
                title={lang.components}
                numResults={components.length}
                numOfSelected={
                  selectedCard &&
                  selectedCard.status === IStatus.READY &&
                  getExpiryStatusMessage(selectedCard.end_date) !==
                    IExpiredStatus.expired
                    ? 1
                    : 0
                }
                maxResults={totalCount}
                onHandleClick={() => navigate(PATHS.COMPONENT_ADD)}
                selectedCards={selectedCard ? [selectedCard.id] : []}
                onBulkReplace={() =>
                  navigate(PATHS.COMPONENT_REPLACE(selectedCard?.id))
                }
                disableBulkAction={!isNotGlobalComponent}
              />
            }
            title="Components"
          />
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} padding={0} spacing={4}>
          <Box flex={0.4} sx={{ maxWidth: '21.7vw' }}>
            <FilterList
              enableSearchArea
              filters={filters}
              totalPages={totalPages}
              updatePage={updateComponentTypesPage}
              onChangeKeyword={onHandleChangeKeyword}
              onRemoveKeyword={onHandleRemoveKeyword}
              onChangeFilter={onHandleChangeFilter}
              showReset
              onReset={onHandleReset}
              hasExactSearch
              onSearchTypeChange={onSearchTypeChange}
              isFuzzySearch={isFuzzySearch}
            />
          </Box>

          <Box flex={1}>
            <SubHeaderWithSorting
              title="Components"
              selectedSortOption={selectedSortOption}
              onSortChange={onSortChange}
              numOfSelected={0}
              totalCount={totalCount}
              numResults={components.length}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              page={page}
            />
            {components?.length === 0 && !isLoadingComponents ? (
              <EmptyState
                isLoading={isLoadingComponents}
                dataToCheck={components}
              />
            ) : (
              <>
                <TableWrapper>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TableBodyContainer>
                      <Table stickyHeader>
                        <CustomTableHead>
                          <TableRow>
                            {columns?.map((column) => (
                              <CustomTableCell
                                key={column.key}
                                width={column.width}>
                                <CustomTableHeadCell label={column.label}>
                                  {column.label}
                                </CustomTableHeadCell>
                              </CustomTableCell>
                            ))}
                          </TableRow>
                        </CustomTableHead>
                        <TableBody>
                          {isLoadingComponents ? (
                            <TableRow>
                              <CustomTableCell
                                colSpan={columns.length}
                                height={120}>
                                <Progress />
                              </CustomTableCell>
                            </TableRow>
                          ) : (
                            components?.map((component: IComponent) => {
                              return (
                                <ComponentTableRow
                                  key={component.id}
                                  row={component}
                                  isHighlighted={
                                    selectedCard?.id === component.id
                                  }
                                  onHighlighted={handleCardClick}
                                  columns={columns}
                                  onHandleCloseSidePanel={
                                    onHandleComponentPreviewSidebarClose
                                  }
                                />
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableBodyContainer>
                    {previewSidebar}
                  </Stack>
                </TableWrapper>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onChange={handlePageChange}
                />
              </>
            )}
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};
